import { useState } from 'react';
import {
  useVisitStandard, useRecordAdjustment, useEndAdjustment,
  useRecordExemption, useEndExemption, type VisitStandard,
} from '../api/visitStandard.ts';
import { ApiError } from '../api/client.ts';
import { formatDate } from '../dates.ts';

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const quiet =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold ' +
  'text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100';

/**
 * How often this client must be seen, and on whose say-so.
 *
 * Three facts of three different kinds, shown as three: the agency's rate for
 * where they live, the minimum a court order requires, and whatever the agency
 * decided for this one client. The panel never collapses them into a single
 * number, because the number alone cannot be argued with and the reasons can.
 *
 * **An exemption standing against a court order is stated first, in the loudest
 * thing on the panel.** It is a lawful decision — the agency must be able to
 * refuse to send staff to somebody who is a danger to them — and it is one the
 * court is owed an explanation for. The same sentence appears here and in the
 * report, from the same view, so the two cannot drift.
 */
export function VisitStandardPanel({ clientId }: { clientId: string }) {
  const std = useVisitStandard(clientId);
  const [form, setForm] = useState<'adjust' | 'exempt' | null>(null);

  if (std.isLoading) {
    return <Shell><p className="text-neutral-500">Loading the visiting standard…</p></Shell>;
  }
  if (!std.data) {
    return <Shell><p className="text-neutral-500">The visiting standard could not be loaded.</p></Shell>;
  }

  const d = std.data;
  const minimum = d.court_minimum.find((m) => m.visits_per_year !== null) ?? null;
  const adjustment = d.adjustment.find((a) => a.is_current) ?? null;
  const exemption = d.exemption.find((e) => e.is_current) ?? null;

  return (
    <Shell>
      {/* The disclosure, first and unmissable. */}
      {d.exempt_against_an_order && (
        <p className="mb-3 rounded-lg border-l-8 border-warning-edge bg-warning-bg px-3 py-2
                      font-semibold text-warning-fg">
          {d.exempt_against_an_order.disclosure}
          <span className="mt-1 block font-normal">
            This must be explained to the court in the next report, and the report will
            not be marked complete until it is.
          </span>
        </p>
      )}

      <p className="text-neutral-900">
        {d.effective?.visits_per_year === 0 ? (
          <span className="font-semibold">Not visited</span>
        ) : (
          <>
            <span className="font-semibold">{d.effective?.visits_per_year} a year</span>
            {d.effective?.interval_days && (
              <span className="text-neutral-600"> · about every {d.effective.interval_days} days</span>
            )}
          </>
        )}
        <span className="text-neutral-600"> · {d.effective?.source}</span>
      </p>
      {d.effective?.note && (
        <p className="mt-0.5 text-sm text-neutral-600">{d.effective.note}</p>
      )}

      <dl className="mt-3 space-y-1.5 text-sm">
        <Row term="Court order">
          {minimum ? (
            <>
              <span className="font-medium">no fewer than {minimum.visits_per_year} a year</span>
              <span className="text-neutral-500">
                {' '}· from {formatDate(minimum.effective_from)}
                {minimum.signed_date ? `, order signed ${formatDate(minimum.signed_date)}` : ''}
              </span>
              {/* presumed is not verified, and the difference is on screen */}
              {minimum.basis !== 'Read from the order' && (
                <span className="block text-amber-800">{minimum.basis}</span>
              )}
            </>
          ) : (
            <span className="text-neutral-500">nothing on record</span>
          )}
        </Row>

        <Row term="Agency">
          <span className="text-neutral-700">
            the rate for this kind of placement, set in Company Policies
          </span>
        </Row>

        <Row term="This client">
          {exemption ? (
            <>
              <span className="font-medium">exempt — {exemption.reason.toLowerCase()}</span>
              <span className="block text-neutral-600">{exemption.note}</span>
              <span className="block text-neutral-500">
                {exemption.granted_by ? `${exemption.granted_by}, ` : ''}
                {formatDate(exemption.granted_on)}
                {exemption.review_on ? ` · to be reviewed ${formatDate(exemption.review_on)}` : ''}
              </span>
            </>
          ) : adjustment ? (
            <>
              <span className="font-medium">
                {adjustment.visits_per_year} a year — {adjustment.reason_source.toLowerCase()}
              </span>
              <span className="block text-neutral-600">{adjustment.note}</span>
              <span className="block text-neutral-500">
                {adjustment.set_by ? `${adjustment.set_by}, ` : ''}
                {formatDate(adjustment.effective_from)}
              </span>
            </>
          ) : (
            <span className="text-neutral-500">no adjustment</span>
          )}
        </Row>
      </dl>

      {d.may_adjust && form === null && (
        <Actions
          clientId={clientId}
          hasAdjustment={Boolean(adjustment)}
          hasExemption={Boolean(exemption)}
          onOpen={setForm}
        />
      )}

      {form === 'adjust' && (
        <AdjustForm clientId={clientId} std={d} onDone={() => setForm(null)} />
      )}
      {form === 'exempt' && (
        <ExemptForm clientId={clientId} std={d} onDone={() => setForm(null)} />
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section className="bg-white lg:rounded-lg lg:shadow">
      <h2 className="px-4 pt-3 pb-1 text-xs font-bold tracking-[0.14em] text-neutral-500 uppercase">
        Visiting standard
      </h2>
      <div className="px-4 pt-1 pb-4">{children}</div>
    </section>
  );
}

function Row({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 text-neutral-500">{term}</dt>
      <dd className="min-w-0 text-neutral-800">{children}</dd>
    </div>
  );
}

function Actions({
  clientId, hasAdjustment, hasExemption, onOpen,
}: {
  clientId: string;
  hasAdjustment: boolean;
  hasExemption: boolean;
  onOpen: (f: 'adjust' | 'exempt') => void;
}) {
  const endAdj = useEndAdjustment(clientId);
  const endEx = useEndExemption(clientId);
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {!hasExemption && (
        <button type="button" onClick={() => onOpen('adjust')} className={quiet}>
          {hasAdjustment ? 'Change the adjustment' : 'Adjust for this client'}
        </button>
      )}
      {hasAdjustment && !hasExemption && (
        <button type="button" disabled={endAdj.isPending}
                onClick={() => endAdj.mutate({ reason: 'Ended from the client page' })}
                className={quiet}>
          Back to the agency rate
        </button>
      )}
      {hasExemption ? (
        <button type="button" disabled={endEx.isPending}
                onClick={() => endEx.mutate({ reason: 'Ended from the client page' })}
                className={quiet}>
          End the exemption
        </button>
      ) : (
        <button type="button" onClick={() => onOpen('exempt')} className={quiet}>
          Exempt from visiting
        </button>
      )}
    </div>
  );
}

function AdjustForm({
  clientId, std, onDone,
}: { clientId: string; std: VisitStandard; onDone: () => void }) {
  const record = useRecordAdjustment(clientId);
  const minimum = std.court_minimum.find((m) => m.visits_per_year !== null) ?? null;
  const [n, setN] = useState('');
  const [source, setSource] = useState('Clinical need');
  const [note, setNote] = useState('');
  const ready = /^\d+$/.test(n) && Number(n) >= 1 && Number(n) <= 52 && note.trim() !== '';

  return (
    <form
      className="mt-3 border-t border-neutral-200 pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        record.mutate(
          { visits_per_year: Number(n), reason_source: source, note: note.trim() },
          { onSuccess: onDone },
        );
      }}
    >
      <label className={label} htmlFor="adj-n">Visits per year for this client</label>
      <input id="adj-n" type="number" min={1} max={52} className={field} value={n}
             onChange={(e) => setN(e.target.value)} />
      {minimum && (
        <p className="mt-1 text-xs text-neutral-500">
          A court order requires no fewer than {minimum.visits_per_year} a year. A lower
          number will be refused — to stop visiting altogether, record an exemption.
        </p>
      )}

      <label className={`${label} mt-3`} htmlFor="adj-src">On what basis</label>
      <select id="adj-src" className={field} value={source}
              onChange={(e) => setSource(e.target.value)}>
        <option>Clinical need</option>
        <option>Agency policy</option>
      </select>

      <label className={`${label} mt-3`} htmlFor="adj-note">Why</label>
      <textarea id="adj-note" className={`${field} min-h-[80px] py-2`} value={note}
                maxLength={4000} onChange={(e) => setNote(e.target.value)} />

      <Failure error={record.error} />
      <Buttons busy={record.isPending} disabled={!ready} verb="Record the adjustment" onCancel={onDone} />
    </form>
  );
}

function ExemptForm({
  clientId, std, onDone,
}: { clientId: string; std: VisitStandard; onDone: () => void }) {
  const record = useRecordExemption(clientId);
  const minimum = std.court_minimum.find((m) => m.visits_per_year !== null) ?? null;
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [review, setReview] = useState('');
  const ready = code !== '' && note.trim() !== '';

  return (
    <form
      className="mt-3 border-t border-neutral-200 pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        record.mutate(
          {
            reason_code: code,
            note: note.trim(),
            ...(review.length === 10 ? { review_on: review } : {}),
          },
          { onSuccess: onDone },
        );
      }}
    >
      <h3 className="font-semibold text-neutral-900">Exempt this client from visiting</h3>

      {minimum && (
        <p className="mt-2 rounded-lg border-l-8 border-warning-edge bg-warning-bg px-3 py-2
                      text-sm text-warning-fg">
          A court order requires no fewer than {minimum.visits_per_year} visits a year for this
          client. The agency may still exempt them — and the next court report will show that
          it has, and will not be complete until the reason is explained to the court.
        </p>
      )}

      <label className={`${label} mt-3`} htmlFor="ex-reason">Reason</label>
      <select id="ex-reason" className={field} value={code}
              onChange={(e) => setCode(e.target.value)}>
        <option value="">Choose…</option>
        {std.exemption_reasons.map((r) => (
          <option key={r.code} value={r.code}>{r.label}</option>
        ))}
      </select>
      {code && (
        <p className="mt-1 text-xs text-neutral-500">
          {std.exemption_reasons.find((r) => r.code === code)?.detail}
        </p>
      )}

      <label className={`${label} mt-3`} htmlFor="ex-note">
        What happened <span className="font-normal text-neutral-500">— required</span>
      </label>
      <textarea id="ex-note" className={`${field} min-h-[80px] py-2`} value={note}
                maxLength={4000} onChange={(e) => setNote(e.target.value)} />

      <label className={`${label} mt-3`} htmlFor="ex-review">
        Review on <span className="font-normal text-neutral-500">— optional</span>
      </label>
      <input id="ex-review" type="date" className={field} value={review}
             onChange={(e) => setReview(e.target.value)} />
      <p className="mt-1 text-xs text-neutral-500">
        A task is raised a fortnight before, so the exemption is looked at again rather
        than left standing.
      </p>

      <Failure error={record.error} />
      <Buttons busy={record.isPending} disabled={!ready} verb="Exempt from visiting" onCancel={onDone} />
    </form>
  );
}

function Buttons({
  busy, disabled, verb, onCancel,
}: { busy: boolean; disabled: boolean; verb: string; onCancel: () => void }) {
  return (
    <div className="mt-3 flex gap-2">
      <button type="submit" disabled={busy || disabled}
              className="min-h-[48px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold text-white
                         disabled:bg-neutral-300">
        {busy ? 'Saving…' : verb}
      </button>
      <button type="button" onClick={onCancel} className={quiet}>Cancel</button>
    </div>
  );
}

function Failure({ error }: { error: Error | null }) {
  if (!error) return null;
  const api = error instanceof ApiError ? error : null;
  return (
    <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-900">
      {error.message}
      {api?.hint && <span className="block text-rose-700">{api.hint}</span>}
    </p>
  );
}
